/**
 * FERP Authentication Hook - Multi-Tenant Security System
 *
 * Provides authentication state and tenant context for life-critical workflows.
 * Supports healthcare (1h), business (2h), and admin (4h) session durations
 * with automatic re-authentication and audit trails.
 *
 * CRITICAL: All data access requires valid tenant context for security isolation.
 */

import { useState, useEffect, useCallback } from 'react';

// Window.__ENV__ is already declared in apps/portal/src/types/global.d.ts
// with the FerpRuntimeEnv interface - no re-declaration needed here

// Authentication state interface
export interface AuthState {
  isAuthenticated: boolean;
  user: AuthUser | null;
  tenant: AuthTenant | null;
  sessionId: string | null;
  expiresAt: Date | null;
  permissions: Permission[];
}

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  userType: 'healthcare' | 'business' | 'admin';
  tenantId: string;
  roles: string[];
  lastLogin: Date;
}

export interface AuthTenant {
  id: string;
  name: string;
  domain: string;
  complianceFramework: string[];
  safetyLevel: 'STANDARD' | 'BUSINESS_CRITICAL' | 'LIFE_CRITICAL';
  features: string[];
}

export interface Permission {
  resource: string;
  actions: string[];
  conditions?: Record<string, unknown>;
}

// Session configuration by user type
const SESSION_DURATIONS = {
  healthcare: 60 * 60 * 1000, // 1 hour for life-critical work
  business: 2 * 60 * 60 * 1000, // 2 hours for business operations
  admin: 4 * 60 * 60 * 1000, // 4 hours for administrative tasks
} as const;

/**
 * useAuth - Authentication and tenant context hook
 *
 * Manages user authentication, tenant isolation, and session security
 * for FERP's multi-tenant life-critical workflow system.
 */
export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    tenant: null,
    sessionId: null,
    expiresAt: null,
    permissions: []
  });

  // Initialize auth state from localStorage/sessionStorage
  useEffect(() => {
    const initializeAuth = () => {
      try {
        const storedAuth = localStorage.getItem('ferp_auth_state');
        if (storedAuth) {
          const parsed = JSON.parse(storedAuth);
          const expiresAt = new Date(parsed.expiresAt);

          // Check if session is still valid
          if (expiresAt > new Date()) {
            setAuthState({
              ...parsed,
              expiresAt
            });
          } else {
            // Session expired, clear stored state
            localStorage.removeItem('ferp_auth_state');
            console.log('[AUTH] Session expired, clearing stored state');
          }
        } else {
          // Development mode - set up mock auth for testing
          setUpMockAuth();
        }
      } catch (error) {
        console.error('[AUTH] Failed to initialize auth state:', error);
        setUpMockAuth();
      }
    };

    initializeAuth();
  }, []);

  // Set up mock authentication for development
  const setUpMockAuth = useCallback(() => {
    // Get tenant ID from environment or use default for development
    // Use window.__ENV__ (runtime) or import.meta.env (Vite build-time) — NOT process.env (Node.js only)
    const envTenantId = window?.__ENV__?.DEV_TENANT_ID;
    const devTenantId: string = (typeof envTenantId === 'string' ? envTenantId : undefined) ||
                       import.meta.env?.VITE_DEV_TENANT_ID ||
                       import.meta.env?.VITE_E2E_TENANT ||
                       'dev-tenant-001';

    const mockUser: AuthUser = {
      id: '2_1',
      email: 'ald@hillsideharmony.com',
      name: 'Phineas Vang',
      userType: 'business',
      tenantId: devTenantId,
      roles: ['user', 'admin'],
      lastLogin: new Date()
    };

    const mockTenant: AuthTenant = {
      id: devTenantId,
      name: 'Development Tenant',
      domain: `${devTenantId}.ferp.local`,
      complianceFramework: ['LIFE_CRITICAL', 'GDPR'],
      safetyLevel: 'LIFE_CRITICAL',
      features: ['workflows', 'safety_monitoring', 'audit_trails']
    };

    const mockSessionId = `dev-session-${Date.now()}`;
    const sessionDuration = SESSION_DURATIONS[mockUser.userType];
    const expiresAt = new Date(Date.now() + sessionDuration);

    const mockAuthState: AuthState = {
      isAuthenticated: true,
      user: mockUser,
      tenant: mockTenant,
      sessionId: mockSessionId,
      expiresAt,
      permissions: [
        { resource: 'workflows', actions: ['read', 'write', 'execute'] },
        { resource: 'entities', actions: ['read', 'write', 'delete'] },
        { resource: 'safety_thresholds', actions: ['read', 'write'] },
        { resource: 'audit_trails', actions: ['read'] }
      ]
    };

    setAuthState(mockAuthState);

    // Store in localStorage for persistence
    localStorage.setItem('ferp_auth_state', JSON.stringify({
      ...mockAuthState,
      expiresAt: expiresAt.toISOString()
    }));

    console.log('[AUTH] Mock authentication initialized for development');
  }, []);

  // Get current tenant ID
  const getCurrentTenantId = useCallback((): string => {
    if (!authState.tenant) {
      throw new Error('No tenant context available. User must be authenticated.');
    }
    return authState.tenant.id;
  }, [authState.tenant]);

  // Get current user ID
  const getCurrentUserId = useCallback((): string => {
    if (!authState.user) {
      throw new Error('No user context available. User must be authenticated.');
    }
    return authState.user.id;
  }, [authState.user]);

  // Get authentication headers for API requests
  const getAuthHeaders = useCallback((): Record<string, string> => {
    const headers: Record<string, string> = {};

    if (authState.sessionId) {
      headers['Authorization'] = `Bearer ${authState.sessionId}`;
    }

    if (authState.tenant) {
      headers['x-tenant'] = authState.tenant.id;
    }

    if (authState.user) {
      headers['x-user-id'] = authState.user.id;
    }

    return headers;
  }, [authState]);

  // Get session ID
  const getSessionId = useCallback((): string | null => {
    return authState.sessionId;
  }, [authState.sessionId]);

  // Check if user has permission for specific action
  const hasPermission = useCallback((resource: string, action: string): boolean => {
    if (!authState.isAuthenticated) {
      return false;
    }

    return authState.permissions.some(permission =>
      permission.resource === resource && permission.actions.includes(action)
    );
  }, [authState.isAuthenticated, authState.permissions]);

  // Login function (in production would call authentication service)
  const login = useCallback(async (email: string, password: string): Promise<void> => {
    try {
      // In production, this would call the authentication API
      console.log('[AUTH] Login attempt for:', email);

      // Mock successful login for development
      if (email && password) {
        setUpMockAuth();
      } else {
        throw new Error('Invalid credentials');
      }
    } catch (error) {
      console.error('[AUTH] Login failed:', error);
      throw error;
    }
  }, [setUpMockAuth]);

  // Logout function
  const logout = useCallback((): void => {
    // Clear stored auth state
    localStorage.removeItem('ferp_auth_state');
    sessionStorage.clear();

    // Reset auth state
    setAuthState({
      isAuthenticated: false,
      user: null,
      tenant: null,
      sessionId: null,
      expiresAt: null,
      permissions: []
    });

    console.log('[AUTH] User logged out');
  }, []);

  // Extend session
  const extendSession = useCallback((): void => {
    if (!authState.user || !authState.isAuthenticated) {
      throw new Error('Cannot extend session: user not authenticated');
    }

    const sessionDuration = SESSION_DURATIONS[authState.user.userType];
    const newExpiresAt = new Date(Date.now() + sessionDuration);

    const updatedAuthState = {
      ...authState,
      expiresAt: newExpiresAt
    };

    setAuthState(updatedAuthState);

    // Update stored state
    localStorage.setItem('ferp_auth_state', JSON.stringify({
      ...updatedAuthState,
      expiresAt: newExpiresAt.toISOString()
    }));

    console.log(`[AUTH] Session extended for ${authState.user.userType} user until ${newExpiresAt.toISOString()}`);
  }, [authState]);

  // Check if session is about to expire (within 5 minutes)
  const isSessionExpiring = useCallback((): boolean => {
    if (!authState.expiresAt) {
      return false;
    }

    const fiveMinutesFromNow = new Date(Date.now() + 5 * 60 * 1000);
    return authState.expiresAt <= fiveMinutesFromNow;
  }, [authState.expiresAt]);

  // Auto-extend session if user is active
  useEffect(() => {
    if (authState.isAuthenticated && isSessionExpiring()) {
      console.log('[AUTH] Session expiring soon, extending automatically');
      extendSession();
    }
  }, [authState.isAuthenticated, isSessionExpiring, extendSession]);

  return {
    // State
    isAuthenticated: authState.isAuthenticated,
    user: authState.user,
    tenant: authState.tenant,
    sessionId: authState.sessionId,
    expiresAt: authState.expiresAt,
    permissions: authState.permissions,

    // Actions
    login,
    logout,
    extendSession,

    // Helpers
    getCurrentTenantId,
    getCurrentUserId,
    getAuthHeaders,
    getSessionId,
    hasPermission,
    isSessionExpiring
  };
}

/**
 * Hook for components that require authentication
 */
export function useRequireAuth() {
  const auth = useAuth();

  useEffect(() => {
    if (!auth.isAuthenticated) {
      console.warn('[AUTH] Component requires authentication but user is not logged in');
    }
  }, [auth.isAuthenticated]);

  return auth;
}

/**
 * Hook for tenant-specific operations
 */
export function useTenantContext() {
  const auth = useAuth();

  if (!auth.tenant) {
    throw new Error('Tenant context required but not available');
  }

  return {
    tenant: auth.tenant,
    tenantId: auth.tenant.id,
    complianceFramework: auth.tenant.complianceFramework,
    safetyLevel: auth.tenant.safetyLevel,
    features: auth.tenant.features
  };
}

export default useAuth;